/**
 * viewing-map.js
 */

import React from 'react';
import axios from 'axios';
import { useMediaQuery, Box, Button } from '@mui/material';
import usePrevious from 'utils/use-previous';
import IconSection from 'components/icon-section';
import FormSectionTitle from 'components/menu-drawer/form-section-title';
import Icon from 'components/icon';
import Spinner from 'components/spinner';
import FeatureBox from 'components/feature-box';
import { TrackContext } from '../track-context';
import { TrackingDrawerContext } from '../input-drawers/drawer-context';
import { DRAWER_TYPE, headerHeight } from '../input-drawers/control-common';
import SelectedPointDrawer from '../input-drawers/selected-point-drawer';
import MarkerDrawer from '../input-drawers/marker-drawer';
import { ViewingMapContext } from './viewing-map-context';
import { TRACK_TYPE } from '../tracking-common';
import { gmapCoords } from './map-common';
import { MapContext } from './map-context';
import { MapTypeControl, MapZoomControl } from './map-controls';
import Map from './map';



// SAVED CODE FOR DETERMINING WHEN AN ELEMENT IS VIEWED IN CASE WE WANT TO RE-IMPLEMENT THE ACCORDION VIEWER
// React.useEffect(() => {
//   if (tracks?.length > 0) {
//     var observer = new IntersectionObserver((entries, observer) => {
//       entries.forEach(entry => {
//         if (entry.intersectionRatio > 0) {
//           setDoRenderMap(true);
//         }
//       })
//     });
//     observer.observe(containerRef.current);
//   }
// }, [tracks]);


const HANDLER_ENDPOINTS = {
  [TRACK_TYPE.DEPLOYMENT]: (deploymentTrackingId) => `/deployment-track/tracking-id/${deploymentTrackingId}`,
  [TRACK_TYPE.LEAD]: (trackId) => `/exercise-lead-track/${trackId}`,
  [TRACK_TYPE.FOLLOW]: (completionTrackingId) => `/exercise-follow-track/tracking-id/${completionTrackingId}`,
};


const MANAGER_ENDPOINTS = {
  [TRACK_TYPE.FOLLOW]: (completionId) => `/training/answer/${completionId}/track`,
  [TRACK_TYPE.DEPLOYMENT]: (deploymentId) => `/deployment/${deploymentId}/track`,
  [TRACK_TYPE.MULTI_DEPLOYMENT]: () => `/deployment/live-tracks`,
};


const CLEAR_CACHE_ENDPOINTS = {
  [TRACK_TYPE.DEPLOYMENT]: (deploymentId) => `/deployment/${deploymentId}/clear-cached-tracking-map`,
  [TRACK_TYPE.MULTI_DEPLOYMENT]: (deploymentId) => `/deployment/${deploymentId}/clear-cached-tracking-map`,
  // [TRACK_TYPE.LEAD]: (trackId) => (answerId) => `/training/answer/${answerId}/clear-cached-tracking-map`,
  [TRACK_TYPE.FOLLOW]: (completionId) => `/training/answer/${completionId}/clear-cached-tracking-map`,
}


const trackRequest = (type, recordId, trackingId, user) => {
  if (user.role.isHandler) {
    return {
      method: 'get',
      url: HANDLER_ENDPOINTS[type](trackingId),
      baseURL: process.env.REACT_APP_TRACKING_HOST
    };
  } else {
    return {
      method: 'get',
      url: MANAGER_ENDPOINTS[type](recordId),
    };
  }
}


export const fetchTracks = async (type, recordId, trackingId, user) => {
  try {
    const tracks = [];
    const dataRequest = trackRequest(type, recordId, trackingId, user)
    const response = await axios(dataRequest);
    if (!response.isSuccess) {
      return false;
    }

    if (type === TRACK_TYPE.MULTI_DEPLOYMENT) {
      response.data.forEach(track => {
        tracks.push(track);
      });
    } else {
      // add lead track if exists
      if (response.data.leadTrack) {
        tracks.push(response.data.leadTrack)
      } 

      tracks.push(response.data);
    }

    return tracks;
  } catch {
    return false;
  }
}


export const MapBase = (props) => {
  let { 
    type, 
    recordId, 
    ...rest
  } = props;
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { tracks, setTracks } = React.useContext(TrackContext);
  const { mapApi, map, selectedPoint, selectedMarkerUuid } = React.useContext(MapContext);
  const { drawerExpansionHeight, setDrawerExpansionHeight  } = React.useContext(TrackingDrawerContext);
  const { setHasReference, isLoading } = React.useContext(ViewingMapContext);
  const prevMap = usePrevious(map);
  const trackIds = React.useMemo(() => tracks.map(t => t.id).join('.'), [tracks]);
  const prevTrackIds = usePrevious(trackIds);
  const activeDrawer = selectedPoint
    ? DRAWER_TYPE.POINT
    : selectedMarkerUuid !== null
      ? DRAWER_TYPE.MARKER
      : null;


  // init / cleanup: reg / dereg reference with context
  React.useEffect(() => {
    setHasReference(true);
    return () => {
      setHasReference(false);
    }
  }, [setHasReference, setTracks]);


  // fit bounds helper
  const fitBounds = React.useCallback(() => {
    const bounds = new mapApi.Bounds();
    tracks.forEach(track => {
      track.points.forEach(point => {
        bounds.extend(gmapCoords(point));
      });
    });
    map.fitBounds(bounds, 30);
  }, [map, mapApi, tracks]);
  

  // re-fit bounds when first track in list changes
  React.useEffect(() => {
    if (
      mapApi 
      && map 
      && trackIds 
      && (map !== prevMap || trackIds !== prevTrackIds)
    ) {
      fitBounds();
    }
  }, [mapApi, map, prevMap, trackIds, prevTrackIds, fitBounds]);


  // set drawer height to 0 if there is no active drawer
  React.useEffect(() => {
    if (!activeDrawer) {
      setDrawerExpansionHeight(0);
    }
  }, [setDrawerExpansionHeight, activeDrawer]);


  const handleMapChange = () => {
    // clear map cache
    if (type && recordId) {
      const url = CLEAR_CACHE_ENDPOINTS[type](recordId);
      axios.post(url);
    }
  };


  if (!tracks) {
    return null;
  } else {
    return (
      <FeatureBox sx={{ height: '100%' }} loading={isLoading}>
        <Box sx={{ 
          height: `calc(100% - ${drawerExpansionHeight > 0 ? drawerExpansionHeight + headerHeight : 0}px)`, 
          width: '100%', 
          position: 'relative',          
          transition: (theme) => `${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
        }}>
          <Map {...rest} />
          {!activeDrawer &&
          <Box sx={{ position: 'absolute', right: 0, bottom: 0, mb: 2, display: 'flex', flexDirection: 'column' }}>
            {!isMobile && <MapZoomControl />}
            <MapTypeControl />
          </Box>}
        </Box>
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, }}>
          <SelectedPointDrawer
            isActive={activeDrawer === DRAWER_TYPE.POINT}
            onSave={handleMapChange}
          />
          <MarkerDrawer
            key={selectedMarkerUuid}
            isActive={activeDrawer === DRAWER_TYPE.MARKER}
            onDelete={handleMapChange}
          />
        </Box>
      </FeatureBox>
    )
  }
}


const DrawerMapViewer = (props) => {
  const { tracks: providedTracks, type, recordId, trackingRecord } = props;
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { drawerExpansionHeight } = React.useContext(TrackingDrawerContext);
  const { setMapParams, isDialogOpen, setIsDialogOpen } = React.useContext(ViewingMapContext);
  const { tracks } = React.useContext(TrackContext);
  const trackingId = trackingRecord?.id;


  // set map context params when drawer map viewer is initialized
  React.useEffect(() => {
    if (type && recordId && trackingId) {
      setMapParams(type, recordId, trackingId, providedTracks);
    }
  }, [type, recordId, trackingId, providedTracks, setMapParams]);


  return (
    <IconSection iconName='map'>
      <Box sx={{ mt: 1 }}>
        <FormSectionTitle
          className='flex-hc'
          sx={{
            textTransform: {
              xs: 'uppercase',
              md: 'none'
            }
          }}
        >
          Tracking Map&nbsp;&nbsp;&nbsp;&nbsp;
          {tracks === undefined && <Spinner size={24} />}
        </FormSectionTitle>        
        {tracks === false && <Box sx={{ color: 'error.main', mt: 1 }}>No tracking map found</Box>}
        {tracks &&
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              color='primary'
              startIcon={<Icon type='fullscreen' size='small' />}
              children='Full Screen'
              onClick={() => setIsDialogOpen(true)}
            />
          </Box>
          <Box 
            sx={{
              position: 'relative', 
              width: '100%', 
              height: isMobile ? 300 : 500 + drawerExpansionHeight, 
              border: 1, 
              borderColor: 'sys.outline',
              transition: theme => `height ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`
            }} 
          >
            {/* we have to hide the base map while dialog is open otherwise some interactions stop working */}
            {!isDialogOpen && <MapBase type={type} recordId={recordId} />}
            {isMobile &&
              <Box
                sx={{ zIndex: 350, position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDialogOpen(true);
                }}
              />
            }
          </Box>
        </>}
      </Box>
    </IconSection>
  );
}


export const DeploymentMap = (props) => {
  if (!props.tracks && !props.trackingRecord?.hasTrack) {
    return null;
  }

  return (
    <DrawerMapViewer type={TRACK_TYPE.DEPLOYMENT} {...props} />
  );
}


export const ExerciseFollowMap = (props) => {
  if (!props.tracks && !props.trackingRecord?.hasTrack) {
    return null;
  }

  return (
    <DrawerMapViewer type={TRACK_TYPE.FOLLOW} {...props} />
  );
}